import {DepartmentsClient} from '@client/departments-client'
import {Sync} from '@client/sync'
import {Lang} from 'yougile-lang'
import {
  DataObjectId,
  Hub,
  LocationFilterItem,
  noBoardContainerId
} from 'yougile-model'
import {Board} from '../../../../extensions/board'
import {globals} from '../../../../extensions/globals-base'
import {Project} from '../../../../extensions/project'

export type ProjectsTree = {
  project: {id: DataObjectId; title: string}
  boards: {
    board: Hub
    columns: Hub[]
  }[]
}[]

const getProjectsTree = (): {tree: ProjectsTree; allIds: Set<DataObjectId>} => {
  const companyId = globals.getCompanyId()
  const hierarchy = DepartmentsClient.getHierarchy(Sync.get({companyId}))
  const allProjects = Project.listProjects(companyId, 'not-archive')
    .sort(Project.projectOrderComparer)
    .map(p => ({
      id: p.id,
      title: p.title,
      companyId: p.companyId
    }))
  allProjects.unshift({
    id: noBoardContainerId,
    title: Lang.msg.statsFilterWithoutBoard,
    companyId
  })
  const allIds = new Set<DataObjectId>()
  const tree = allProjects.map(project => {
    allIds.add(project.id)
    const boards = Project.listBoards(project, true).map(board => {
      allIds.add(board.id)
      const columns = Board.listColumns(board, {
        hierarchy,
        noMirrors: true,
        sorted: true
      })
      columns.forEach(x => allIds.add(x.id))
      return {board, columns}
    })
    return {project, boards}
  })
  return {tree, allIds}
}

interface BoardResult {
  boardId: DataObjectId
  allBoardColumns: Hub[]
  selectedColumns: DataObjectId[]
  filters: DataObjectId[]
}

const getFilteredProjectBoards = (
  boards: {board: Hub; columns: Hub[]}[],
  ids: DataObjectId[]
): [boardResults: BoardResult[], remainingIds: DataObjectId[]] => {
  const idSet = new Set(ids)
  const processedIds = new Set<DataObjectId>()
  const boardResults: BoardResult[] = []

  for (const item of boards) {
    const {board, columns} = item
    processedIds.add(board.id)
    columns.forEach(c => processedIds.add(c.id))
    const filteredColumns = columns.filter(c => idSet.has(c.id))
    const filterdColumnIds = filteredColumns.map(c => c.id)
    const hasBoard = idSet.has(board.id)

    if (hasBoard || filteredColumns.length) {
      boardResults.push({
        boardId: board.id,
        allBoardColumns: columns,
        selectedColumns: filterdColumnIds,
        filters: filterdColumnIds
      })
    }
  }
  const remainingIds = ids.filter(id => !processedIds.has(id))
  return [boardResults, remainingIds]
}

const getFiltersRows = (tree: ProjectsTree, ids: DataObjectId[]) => {
  let remainigIds = [...ids]
  return [
    tree.flatMap(({project, boards}) => {
      if (!remainigIds.length) {
        return []
      }
      const hasProjectId = remainigIds[0] === project.id
      if (hasProjectId) {
        remainigIds.splice(0, 1)
      }
      const [projectBoards, updatedIds] = hasProjectId
        ? [
            [
              {
                boardId: undefined,
                selectedColumns: [],
                allBoardColumns: [],
                filters: [project.id]
              }
            ],
            remainigIds
          ]
        : getFilteredProjectBoards(boards, remainigIds)
      remainigIds = updatedIds
      return projectBoards.map(board => ({
        ...board,
        projectId: project.id,
        boards,
        hasProjectId
      }))
    }),
    remainigIds
  ] as const
}

export const getLocationFilterRows = (
  filters: LocationFilterItem[],
  initialRow = false
) => {
  const {tree, allIds} = getProjectsTree()
  let filterIdsInTree = filters
    .filter(i => allIds.has(i.parent))
    .map(i => i.parent)

  const rows: ReturnType<typeof getFiltersRows>[0] = []
  let count = 0
  while (filterIdsInTree.length > 0 || count < filters.length) {
    count += 1
    const [filterRows, remainingIds] = getFiltersRows(tree, filterIdsInTree)
    filterIdsInTree = remainingIds
    rows.push(...filterRows)
  }
  if (initialRow && !rows.length) {
    rows.push({
      allBoardColumns: [],
      boardId: undefined,
      boards: [],
      filters: [],
      hasProjectId: false,
      projectId: undefined,
      selectedColumns: []
    })
  }
  return {rows, tree}
}
